import { request } from '../../../service/request';

const DEFAULT_ICE_SERVERS = [{ urls: 'stun:stun.l.google.com:19302' }];
const DEFAULT_VOICE_CALL_CONFIG_TIMEOUT_MS = 1500;

const fallbackVoiceCallConfig = () => ({
  enabled: true,
  iceServers: DEFAULT_ICE_SERVERS,
});

const withTimeout = (promise, timeoutMs) =>
  new Promise((resolve, reject) => {
    const normalizedTimeoutMs = Number(timeoutMs);
    if (!Number.isFinite(normalizedTimeoutMs) || normalizedTimeoutMs <= 0) {
      Promise.resolve(promise).then(resolve, reject);
      return;
    }

    let settled = false;
    const timer = globalThis.setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error('Voice call config timed out'));
    }, normalizedTimeoutMs);

    Promise.resolve(promise).then(
      (value) => {
        if (settled) return;
        settled = true;
        globalThis.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        if (settled) return;
        settled = true;
        globalThis.clearTimeout(timer);
        reject(error);
      },
    );
  });

const normalizeIceServer = (server = null) => {
  if (!server || typeof server !== 'object') return null;
  const urls = Array.isArray(server.urls)
    ? server.urls.map((url) => String(url || '').trim()).filter(Boolean)
    : String(server.urls || '').trim();
  if ((Array.isArray(urls) && urls.length === 0) || (!Array.isArray(urls) && !urls)) {
    return null;
  }
  return {
    urls,
    ...(server.username ? { username: String(server.username) } : {}),
    ...(server.credential ? { credential: String(server.credential) } : {}),
  };
};

export const loadMessengerV2VoiceCallConfig = async ({
  conversationId,
  timeoutMs = DEFAULT_VOICE_CALL_CONFIG_TIMEOUT_MS,
} = {}) => {
  const normalizedConversationId = String(conversationId || '').trim();
  if (!normalizedConversationId) {
    return fallbackVoiceCallConfig();
  }

  try {
    const response = await withTimeout(
      request(
        `/conversations/${encodeURIComponent(normalizedConversationId)}/calls/config`,
        {},
        'get',
        {},
        { cache: 'no-store' },
      ),
      timeoutMs,
    );
    const payload = response?.data?.voice_call || response?.data || {};
    const iceServers = Array.isArray(payload.ice_servers)
      ? payload.ice_servers.map(normalizeIceServer).filter(Boolean)
      : [];
    return {
      enabled: payload.enabled !== false,
      iceServers: iceServers.length ? iceServers : DEFAULT_ICE_SERVERS,
    };
  } catch {
    return fallbackVoiceCallConfig();
  }
};

export { DEFAULT_ICE_SERVERS };
